import { memoize } from '@exodus/basic-utils';
import type NumberUnit from '@exodus/currency';
import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import selectors from '../../selectors.js';

type Params = { assetName: string; walletAccount: string };

type GetStakableSelector = Selector<unknown, (params: Params) => NumberUnit>;
type GetSpendableSelector = Selector<unknown, (params: Params) => NumberUnit>;

export type GetSpendableBalanceFn = (
  params: Params,
  options?: { forStaking?: boolean },
) => NumberUnit;

const getSpendableBalanceSelector = createSelector(
  selectors.balances.getStakable as GetStakableSelector,
  selectors.balances.getSpendable as GetSpendableSelector,
  (getStakable, getSpendable): GetSpendableBalanceFn =>
    memoize(
      (
        { assetName, walletAccount }: Parameters<GetSpendableBalanceFn>[0],
        { forStaking = false }: Parameters<GetSpendableBalanceFn>[1] = {},
      ): ReturnType<GetSpendableBalanceFn> => {
        const stakable =
          forStaking && getStakable({ assetName, walletAccount });
        if (stakable) {
          return stakable.clampLowerZero();
        }
        return getSpendable({ assetName, walletAccount }).clampLowerZero();
      },
      ({ assetName, walletAccount }: Params, options = {}) =>
        `${assetName}_${walletAccount}_${JSON.stringify(options)}`,
    ),
);

export default getSpendableBalanceSelector;
